import { EpicPolicy } from '@/types/tower/Domain';

export interface PoliciesResponse {
  policies: PolicyWithChildren[];
  stats: PolicyStats;
}

export interface PolicyStats {
  maxRetention?: number;
  maxAttachmentPoint?: number;
  totalCount?: number;
  totalPremium?: number;
}

export interface PolicyWithChildren extends EpicPolicy {
  children?: EpicPolicy[] | 'loading'
}

/**
 * Builds policies as a hierarchy. Used when results are already sorted by an API.
 */
export function GetPoliciesAsHierarchy(policies: EpicPolicy[]): PolicyWithChildren[] {
  const parents = policies.filter(x => !x.isChildPolicy);
  return parents.map(parent => {
    return { ...parent, children: policies.filter(x => x.uniqPolicy === parent.uniqPolicy && x.isChildPolicy && x !== parent) };
  });
}

/**
 * Builds policies as a hierarchy. Used when results are not sorted by an API.
 * This method is faster than GetPoliciesAsHierarchy, but the return policies will
 * need to be resorted when returned.
 */
export function PoliciesAsHierarchy(policies: EpicPolicy[]): PolicyWithChildren[] {
  const sorted = [...policies].sort((a, b) => a.policyID.localeCompare(b.policyID));
  const result: PolicyWithChildren[] = [];
  let currentPolicy: PolicyWithChildren | undefined = undefined;
  for (const policy of sorted) {
    if (policy.isChildPolicy) {
      if (currentPolicy && Array.isArray(currentPolicy.children)) {
        currentPolicy.children.push(policy);
      }
    } else {
      if (currentPolicy) {
        result.push(currentPolicy);
      }
      currentPolicy = { ...policy, children: [] } as PolicyWithChildren;
    }
  }
  if (currentPolicy) {
    result.push(currentPolicy);
  }
  return result;
}

export function FlattenPolicies(policies: PolicyWithChildren[]): EpicPolicy[] {
  return policies.reduce((acc, p) => {
    const { children, ...policy } = p;
    acc.push(policy);
    if (Array.isArray(children)) {
      children.forEach(c => acc.push(c));
    }
    return acc;
  }, [] as EpicPolicy[]);
}
